/**
 * SupplierEditorSheet — Sheet latéral création/édition fournisseur.
 * Miroir strict de ClientEditorSheet.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
SheetDescription, } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Save, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", phone: "", contact_email: "", contact_name: "",
  address: "", postal_code: "", city: "", country: "France",
  siren: "", siret: "", vat_number: "", iban: "", notes: "",
};

export default function SupplierEditorSheet({ supplierId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!supplierId;

  useEffect(() => {
    if (!open) return;
    if (!supplierId) { setForm(EMPTY); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suppliers/${supplierId}`);
        setForm({ ...EMPTY, ...data.supplier });
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [supplierId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === "string" && v === "" ? null : v]),
      );
      payload.name = form.name.trim();
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/suppliers/${supplierId}`, payload);
        saved = data.supplier;
      } else {
        const { data } = await api.post("/suppliers", payload);
        saved = data.supplier;
      }
      toast.success(isEdit ? "Fournisseur modifié" : "Fournisseur créé");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[540px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <SheetTitle className="flex items-center gap-2"
                       style={{ fontFamily: "Manrope, sans-serif" }}>
            <Building2 className="h-5 w-5 text-slate-500" />
            {isEdit ? "Modifier fournisseur" : "Nouveau fournisseur"}
          </SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-6 py-5">
          {loading ? <div className="skeleton h-40 rounded-lg" /> : (
            <div className="space-y-4">
              <div><Label className="text-xs">Nom / Raison sociale *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                        className="mt-1" data-testid="supplier-editor-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">SIRET</Label>
                  <Input value={form.siret || ""} onChange={(e) => set("siret", e.target.value)}
                          className="mt-1 font-mono" placeholder="14 chiffres"
                          data-testid="supplier-editor-siret" />
                </div>
                <div><Label className="text-xs">SIREN</Label>
                  <Input value={form.siren || ""} onChange={(e) => set("siren", e.target.value)}
                          className="mt-1 font-mono" placeholder="9 chiffres" />
                </div>
              </div>
              <div><Label className="text-xs">N° TVA intracommunautaire</Label>
                <Input value={form.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)}
                        className="mt-1 font-mono" placeholder="FR12345678900" />
              </div>
              <div><Label className="text-xs">IBAN</Label>
                <Input value={form.iban || ""} onChange={(e) => set("iban", e.target.value)}
                        className="mt-1 font-mono" placeholder="FR76…"
                        data-testid="supplier-editor-iban" />
                <p className="mt-1 text-[10px] text-slate-400">Chiffré au repos (tenant DEK).</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Contact nom</Label>
                  <Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)}
                          className="mt-1" />
                </div>
                <div><Label className="text-xs">Email contact</Label>
                  <Input type="email" value={form.contact_email || ""}
                          onChange={(e) => set("contact_email", e.target.value)}
                          className="mt-1" />
                </div>
              </div>
              <div><Label className="text-xs">Téléphone</Label>
                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
                        className="mt-1" />
              </div>
              <div><Label className="text-xs">Adresse</Label>
                <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)}
                        className="mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">CP</Label>
                  <Input value={form.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)}
                          className="mt-1 font-mono" />
                </div>
                <div className="col-span-2"><Label className="text-xs">Ville</Label>
                  <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)}
                          className="mt-1" />
                </div>
              </div>
              <div><Label className="text-xs">Notes internes</Label>
                <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
                           className="mt-1 min-h-[80px]" />
              </div>
            </div>
          )}
        </ScrollArea>
        <div className="border-t border-slate-200 px-6 py-3 shrink-0 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving}
                   className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="supplier-editor-save-btn">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
